import React, { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';

import { Avatar, AvatarFallback, AvatarImage } from '@buildoutinc/blueprint-react/ui/Avatar';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@buildoutinc/blueprint-react/ui/Table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@buildoutinc/blueprint-react/ui/DropdownMenu';

import { patch } from 'helpers/api';
import { getInitials } from 'helpers/string_helpers';
import { showSnackbar } from 'components/ui/snackbar/helper';

interface Account {
  id: number;
  type: string;
  name: string;
  title: string | null;
  office: string | null;
  email: string;
  phone: string;
  permissions: string[];
  isPrimary: boolean;
  isCorporate: boolean;
  photoUrl: string | null;
  markPrimaryPath: string;
}

interface Props {
  accounts: Account[];
}

export default function SwitchAccounts({ accounts: initialAccounts }: Props) {
  const [accounts, setAccounts] = useState(initialAccounts);
  const [query, setQuery] = useState('');
  const [pendingId, setPendingId] = useState<number | null>(null);

  const visibleAccounts = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return accounts;

    return accounts.filter(account => (
      [account.name, account.office, account.email].some(
        field => field?.toLowerCase().includes(term),
      )
    ));
  }, [accounts, query]);

  const markPrimary = (account: Account) => {
    setPendingId(account.id);

    patch(account.markPrimaryPath, {})
      .then(() => {
        setAccounts(prev => prev.map(a => ({
          ...a,
          isPrimary: a.id === account.id && a.type === account.type,
        })));
      })
      .catch(() => {
        showSnackbar({ text: 'Something went wrong updating your primary account.', variant: 'danger' });
      })
      .finally(() => setPendingId(null));
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <h4 className="mb-1">Switch Accounts</h4>
        <p className="text-muted mb-0">Accounts you belong to. Mark one as your primary account.</p>
      </div>

      <Input
        placeholder="Search"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Office</TableHead>
            <TableHead>Email Address</TableHead>
            <TableHead>Phone Number</TableHead>
            <TableHead>Permissions</TableHead>
            <TableHead aria-label="Actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleAccounts.map(account => (
            <TableRow key={account.id}>
              <TableCell>
                <div className="d-flex align-items-center gap-2">
                  <Avatar>
                    {account.photoUrl && <AvatarImage alt={account.name} src={account.photoUrl} />}
                    <AvatarFallback>{getInitials(account.name)}</AvatarFallback>
                  </Avatar>
                  <div className="d-flex flex-column">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold">{account.name}</span>
                      {account.isPrimary && <Badge variant="secondary">Primary</Badge>}
                    </div>
                    {account.isCorporate
                      ? <span className="text-muted small">Corporate User</span>
                      : account.title && <span className="text-muted small">{account.title}</span>}
                  </div>
                </div>
              </TableCell>
              <TableCell>{account.office}</TableCell>
              <TableCell>{account.email}</TableCell>
              <TableCell>{account.phone}</TableCell>
              <TableCell>
                <div className="d-flex gap-1">
                  {account.permissions.map(permission => (
                    <span className="text-primary small fw-semibold" key={permission}>{permission}</span>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                {!account.isPrimary && (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      aria-label={`Actions for ${account.name}`}
                      className="btn btn-sm btn-light"
                      disabled={pendingId === account.id}
                    >
                      <FontAwesomeIcon icon={faEllipsisVertical} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => markPrimary(account)}>
                        Set primary
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
